import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DateField } from '@/components/ui/date-time-field';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import MasterItemSummary from '@/components/master/MasterItemSummary';
import { registrationApi } from './registrationApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEDGER_SCREEN = {
  mfg: '/mfg/items', eng: '/engineering/assets', office: '/bihin/items',
  equip: '/mfg/equipment', it: '/it/assets',
};

/**
 * 「交換部品を依頼」 — 設備 / 在庫品目 / 技術資産 の詳細から出す（2026-06）。
 *
 * R3: **まずマスターから選ぶ**（`MasterItemPicker` ひとつ）。選んだら 単位・入数・
 * メーカー・仕入先・直近価格 はマスターの値を読み取り専用で見せる（直すのは
 * 「マスターを編集」から）。在庫で足りていれば 出庫 を先に勧める。
 * 自社製作の部品は **製作依頼**（TECHS に出さない）。購入品は 部品受注入力（REQ）。
 */
export const ReplacementPartButton = ({ originType, originId, originLabel, testId = 'replacement-part' }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [methods, setMethods] = useState([]);
  const [pastParts, setPastParts] = useState([]);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({
    make_type: 'purchased', name: '', part_no: '', qty: 1, uom: '個',
    make_method: '', needed_by: '', notes: '',
  });

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/engineering/fabrication/methods`)
      .then((r) => setMethods(r.data || [])).catch(() => setMethods([]));
    if (originType === 'equipment' && originId) {
      axios.get(`${API}/mfg/equipment/${originId}/parts`)
        .then((r) => setPastParts(r.data || [])).catch(() => setPastParts([]));
    }
  }, [open, originType, originId]);

  const pick = (r) => {
    setPicked(r);
    setForm((f) => ({
      ...f, name: r.name || '', part_no: r.sku || '', uom: r.uom || '個',
      make_type: r.make_type === 'in_house' ? 'in_house' : f.make_type,
    }));
  };

  const unlink = () => { setPicked(null); setForm((f) => ({ ...f, name: '', part_no: '' })); };

  const enoughStock = picked && picked.on_hand != null
    && Number(picked.on_hand) >= (Number(form.qty) || 1);

  const issueFromStock = async () => {
    setBusy(true);
    try {
      const register = picked.source === 'mfg' ? 'mfg' : picked.source === 'eng' ? 'eng' : picked.source;
      await axios.post(`${API}/item-lifecycle/${register}/${picked.id}/issue`, {
        qty: Number(form.qty) || 1,
        note: `交換部品: ${originLabel || ''} ${form.notes || ''}`.trim(),
      });
      toast.success(`在庫から ${form.qty}${form.uom} 出庫しました`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '出庫できませんでした');
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('品目を選ぶか、新規品として登録してください'); return; }
    setBusy(true);
    try {
      if (form.make_type === 'in_house') {
        const r = await axios.post(`${API}/engineering/fabrication`, {
          name: form.name.trim(), part_no: form.part_no || null,
          item_id: picked?.id || null,
          item_source: picked?.source === 'mfg' ? 'mfg' : picked?.source === 'eng' ? 'eng' : null,
          qty: Number(form.qty) || 1, uom: form.uom || '個',
          make_method: form.make_method || null,
          origin_type: originType, origin_id: originId, origin_label: originLabel,
          needed_by: form.needed_by || null,
          notes: `交換部品: ${originLabel || ''} ${form.notes || ''}`.trim(),
        });
        toast.success(`製作依頼 ${r.data.fab_no} を作成しました`);
      } else {
        const r = await axios.post(`${API}/shizai/my-requests`, {
          priority: 'normal',
          order_name: `交換部品: ${originLabel || form.name}`.slice(0, 120),
          needed_by: form.needed_by || null,
          lines: [{
            name: form.name.trim(), part_no: form.part_no || null,
            item_id: picked?.id || null,
            item_source: picked?.source === 'mfg' ? 'mfg' : picked?.source === 'eng' ? 'eng' : null,
            origin_type: originType, origin_id: originId,
            qty: Number(form.qty) || 1, uom: form.uom || '個',
            notes: `交換部品 / 依頼元: ${originLabel || originId} ${form.notes || ''}`.trim(),
          }],
        });
        toast.success(`部品依頼 ${r.data.request_no || ''} を作成しました`);
      }
      setOpen(false);
      setPicked(null);
      setForm({ ...form, name: '', part_no: '', notes: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '依頼の作成に失敗しました');
    } finally { setBusy(false); }
  };

  const ro = (label, val) => (
    <div>
      <p className="text-[11px] text-zinc-400">{label}</p>
      <p className="text-[13px] text-zinc-800" data-no-translate>{val || '—'}</p>
    </div>
  );

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        className="border-sky-200 text-sky-700 hover:bg-sky-50" data-testid={`${testId}-btn`}>
        <Wrench size={14} className="mr-1" /> 交換部品を依頼
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>交換部品を依頼</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="rounded-lg bg-zinc-50 px-3 py-2 text-[12px] text-zinc-600" data-testid={`${testId}-origin`}>
              依頼元: {originLabel || originId}
            </p>

            {/* この設備の過去の部品を先に出す（探さずに選べるように） */}
            {!picked && pastParts.length > 0 && (
              <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2"
                data-testid={`${testId}-past`}>
                <p className="text-[11px] font-semibold text-teal-900">この設備の過去の部品</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {pastParts.slice(0, 8).map((sp, i) => (
                    <button key={`${sp.item_id || sp.name}-${i}`} type="button"
                      onClick={() => (sp.item_id
                        ? registrationApi.masterSearch(sp.sku || sp.name)
                          .then((rows) => pick((rows || []).find((x) => x.id === sp.item_id) || {
                            id: sp.item_id, source: sp.source, name: sp.name, sku: sp.sku, uom: sp.uom,
                          }))
                          .catch(() => pick({ id: sp.item_id, source: sp.source, name: sp.name, sku: sp.sku, uom: sp.uom }))
                        : setForm((f) => ({ ...f, name: sp.name || '', part_no: sp.sku || '', uom: sp.uom || '個' })))}
                      className="rounded-full border border-teal-300 bg-white px-2.5 py-1 text-[11px] text-teal-900 hover:bg-teal-100"
                      data-testid={`${testId}-past-${i}`}>
                      {sp.name}{sp.sku ? `（${sp.sku}）` : ''}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <MasterItemPicker
              testIdPrefix={`${testId}-item`}
              label="品目（マスターから選ぶ）"
              placeholder="品番・品名・型式・メーカーで探す"
              helper="まずマスターから選びます。無ければ「新規品として登録」から台帳に入れてください"
              search={registrationApi.masterSearch}
              sources={['mfg', 'eng', 'office']}
              minChars={1}
              onPick={pick}
              linked={picked ? { label: picked.name, sku: picked.sku, sourceLabel: picked.source_label } : null}
              onUnlink={unlink}
              unlinkLabel="選び直す"
              emptyText="該当する品目がありません。新規品として登録してください。"
              emptyAction={(
                <Button size="sm" variant="outline" data-testid={`${testId}-new-item`}
                  onClick={() => window.open('/engineering/assets?new=1', '_blank', 'noopener')}>
                  新規品として登録
                </Button>
              )}
            />

            {picked && (
              <MasterItemSummary item={picked} testId={`${testId}-master-fields`} />
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">数量</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input type="number" min="1" value={form.qty}
                    onChange={(e) => setForm({ ...form, qty: e.target.value })}
                    className="h-10" data-testid={`${testId}-qty`} />
                  <span className="text-sm text-zinc-600" data-testid={`${testId}-uom`}>
                    {picked ? (picked.uom || '個') : form.uom}
                  </span>
                </div>
              </div>
              {!picked && (
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">品番</Label>
                  <Input value={form.part_no}
                    onChange={(e) => setForm({ ...form, part_no: e.target.value })}
                    className="h-10 mt-1 font-mono" data-testid={`${testId}-part-no`} />
                </div>
              )}
            </div>

            {enoughStock && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
                data-testid={`${testId}-stock-hint`}>
                <p className="text-[12px] text-emerald-900">
                  在庫が {picked.on_hand}{picked.uom || ''} あります。頼まずに出庫できます。
                </p>
                <Button size="sm" className="mt-2 h-8 bg-emerald-700 text-xs hover:bg-emerald-800"
                  disabled={busy} onClick={issueFromStock} data-testid={`${testId}-issue`}>
                  在庫から出庫する
                </Button>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-zinc-600">手配区分</Label>
              <select value={form.make_type} onChange={(e) => setForm({ ...form, make_type: e.target.value })}
                className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                data-testid={`${testId}-make-type`}>
                <option value="purchased">購入品（部品受注入力へ）</option>
                <option value="in_house">自社製作（製作依頼へ）</option>
              </select>
              <p className="mt-1 text-[11px] text-zinc-400">
                自社製作はTECHSに発注されません。製作依頼として技術で作ります
              </p>
            </div>
            {form.make_type === 'in_house' && (
              <div>
                <Label className="text-xs font-semibold text-zinc-600">製作方法</Label>
                <select value={form.make_method} onChange={(e) => setForm({ ...form, make_method: e.target.value })}
                  className="w-full h-10 mt-1 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                  data-testid={`${testId}-method`}>
                  <option value="">（未選択）</option>
                  {methods.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-zinc-600">希望納期</Label>
              <DateField value={form.needed_by} onChange={(v) => setForm({ ...form, needed_by: v })}
                className="mt-1" inputClassName="h-10" testId={`${testId}-needed-by`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
            <Button onClick={submit} disabled={busy} className="bg-teal-700 hover:bg-teal-800"
              data-testid={`${testId}-submit`}>{busy ? '作成中…' : '依頼する'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReplacementPartButton;
